import React, { useContext, useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { PulseLeadIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import { SpinnerDarkIcon } from 'app/components/shared/icons';
import EditPencilIcon from 'app/components/shared/icons/edit-pencil';
import useOnClickOutside from 'hooks/click-outside';
import { postPulseDescription } from 'services/profile-editor';

import PeopleDrawerContactIcon from '../PeopleDrawerContactIcon';
import { PeopleDrawerContext } from '../PeopleDrawerContext';
import {
  PeopleDrawerPulseLeadDescription,
  PeopleDrawerPulseLeadLoadingContainer,
  PeopleDrawerPulseLeadTitle,
  PeopleDrawerPulseLeadTitleContainer,
  PeopleDrawerPulseLeadTitleWrapper,
  PeopleDrawerPulseLeadWrapper,
} from './styled';

export default function PeopleDrawerPulseLead() {
  const { person, isCurrentUser } = useContext(PeopleDrawerContext);
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(person?.info?.pulse_description || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    setDescription(person?.info?.pulse_description || '');
  }, [person?.info?.pulse_description]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      );
    }
  }, [isEditing]);

  const handlePostPulseDescription = async (pulseDescription: string) => {
    const response = await postPulseDescription({ pulse_description: pulseDescription });
    if (!response.ok) {
      throw new Error(response.originalError?.message);
    }
    return response.data;
  };

  const { mutate, isLoading } = useMutation(handlePostPulseDescription, {
    onSuccess: () => {
      isSavingRef.current = false;
      setIsEditing(false);
      // Invalidate the person drawer query to refresh the data
      if (person?.info?.uuid) {
        queryClient.invalidateQueries(['collective-index-person-drawer', person.info.uuid]);
      }
    },
    onError: () => {
      isSavingRef.current = false;
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    // Prevent multiple calls
    if (isLoading || isSavingRef.current) {
      return;
    }

    if (description !== (person?.info?.pulse_description || '')) {
      isSavingRef.current = true;
      mutate(description);
    } else {
      setIsEditing(false);
    }
  };

  useOnClickOutside(containerRef, () => {
    if (isEditing && !isLoading && !isSavingRef.current) {
      handleSave();
    }
  });

  const handleStartEditing = () => {
    if (isCurrentUser && !isEditing) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (!isLoading && !isSavingRef.current) {
        handleSave();
      }
    }
    if (e.key === 'Escape') {
      if (!isLoading && !isSavingRef.current) {
        setDescription(person?.info?.pulse_description || '');
        setIsEditing(false);
      }
    }
  };

  const renderDescriptionContent = () => {
    if (isEditing && isCurrentUser) {
      if (isLoading) {
        return (
          <PeopleDrawerPulseLeadLoadingContainer>
            <SpinnerDarkIcon width={24} height={24} fill="var(--color-primary)" />
          </PeopleDrawerPulseLeadLoadingContainer>
        );
      }
      return (
        <textarea
          ref={textareaRef}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          style={{
            fontFamily: 'inherit',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: 'normal',
            color: 'var(--color-text)',
            background: 'transparent',
            border: '1px solid var(--color-primary)',
            borderRadius: '4px',
            padding: '8px',
            width: '100%',
            minHeight: '60px',
            resize: 'vertical',
            outline: 'none',
          }}
        />
      );
    }
    return (
      <PeopleDrawerPulseLeadDescription
        onClick={handleStartEditing}
        style={isCurrentUser ? { cursor: 'pointer' } : {}}
      >
        {description}
      </PeopleDrawerPulseLeadDescription>
    );
  };

  return (
    <PeopleDrawerPulseLeadWrapper ref={containerRef}>
      <PeopleDrawerPulseLeadTitleWrapper>
        <PeopleDrawerPulseLeadTitleContainer>
          <PulseLeadIcon width={20} height={20} />
          <PeopleDrawerPulseLeadTitle>
            {person?.info.name?.split(' ')[0]} is a Pulse lead:
          </PeopleDrawerPulseLeadTitle>
        </PeopleDrawerPulseLeadTitleContainer>
        {isCurrentUser ? (
          <button
            onClick={handleStartEditing}
            type="button"
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <EditPencilIcon width={20} height={20} fill="var(--color-primary)" />
          </button>
        ) : (
          <PeopleDrawerContactIcon />
        )}
      </PeopleDrawerPulseLeadTitleWrapper>
      {renderDescriptionContent()}
    </PeopleDrawerPulseLeadWrapper>
  );
}
